/**
 * Vuex module for the SecurOne administration view (namespaced: "securone").
 * Holds the capability object from GET /securone/context - who am I here
 * (kind), what may I write (canWrite), which departments exist - so every
 * component asks the store, never a role string (plan §7 rule 1).
 *
 * The server is the authority; this object only decides what to render.
 * Nothing here is persisted: it is re-read from the server on every load.
 */
import api, { setOrganisation } from "./securone.api";

const state = () => ({
  context: null,
  loading: false,
  error: null,
  // the department the user arrived with (?unit=) - pre-selects list filters
  departmentFilter: null,
  // bumps on every securone.admin.changed so open lists refresh
  adminVersion: 0,
});

const getters = {
  enabled: (s) => !!(s.context && s.context.addonEnabled),
  context: (s) => s.context,
  kind: (s) => (s.context ? s.context.actor.kind : null),
  scope: (s) => (s.context ? s.context.actor.scope : null),
  isSecurdia: (s) => !!(s.context && s.context.actor.kind === "securdia_admin"),
  isPrincipal: (s) => !!(s.context && s.context.actor.isPrincipal),
  // customer users may write a free-text alarm on the spot (server rule canRaiseFreeText)
  canRaiseFreeText: (s) => !!(s.context && s.context.canRaiseFreeText),
  canWrite: (s) => (s.context ? s.context.canWrite : {}),
  // may this actor administer anything at all here (any create control)
  canAdminister: (s, g) => Object.values(g.canWrite).some(Boolean),
  me: (s) => (s.context ? s.context.me : null),
  myGroupIds: (s) => (s.context && s.context.me ? s.context.me.groups : []),
  customer: (s) => (s.context ? s.context.customer : null),
  // { total, used, remaining } for Securdia admins, null otherwise (total null = not set on the customer)
  licenses: (s) => (s.context && s.context.licenses ? s.context.licenses : null),
  organisations: (s) => (s.context ? s.context.organisations : []),
  // departments this actor may see / pick from (own for department and basic users, all otherwise)
  departments: (s) => (s.context ? s.context.departments : []),
  actorDepartmentIds: (s) => (s.context ? s.context.actor.departmentIds : []),
  // plan §7 rule 3: a department column when there is more than one department;
  // a filter control only for actors who are not pre-filtered (customer users, Securdia)
  showDepartmentColumn: (s, g) => g.departments.length > 1,
  showDepartmentFilter: (s, g) => g.departments.length > 1 && g.scope !== "departments",
  // plan §7 rule 2: prefilled as static text when there is exactly one department
  singleDepartment: (s, g) => (g.departments.length === 1 ? g.departments[0] : null),
  departmentFilter: (s) => s.departmentFilter,
  departmentName: (s, g) => (id) => {
    const d = g.departments.find((x) => String(x._id) === String(id));
    return d ? d.name : "";
  },
};

const mutations = {
  SET_CONTEXT(s, context) {
    s.context = context;
  },
  SET_LOADING(s, value) {
    s.loading = value;
  },
  SET_ERROR(s, error) {
    s.error = error;
  },
  SET_DEPARTMENT_FILTER(s, id) {
    s.departmentFilter = id || null;
  },
  BUMP_ADMIN(s) {
    s.adminVersion += 1;
  },
  RESET(s) {
    Object.assign(s, state());
  },
};

const actions = {
  async loadContext({ commit }, { customerId = null, unitId = null } = {}) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const context = await api.context(customerId, unitId);
      setOrganisation(context.customer ? context.customer._id : null);
      commit("SET_CONTEXT", context);
      return context;
    } catch (error) {
      const errorId = error && error.response && error.response.data ? error.response.data.errorId : null;
      // 404: the surface does not exist here (absent, not disabled); 403: not this role's feature
      commit("SET_CONTEXT", { addonEnabled: false, errorId, actor: { kind: null, scope: null, departmentIds: [], isPrincipal: false }, canWrite: {}, departments: [], organisations: [], me: null, customer: null });
      commit("SET_ERROR", errorId || "load_failed");
      setOrganisation(null);
      return null;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  selectOrganisation({ dispatch }, customerId) {
    return dispatch("loadContext", { customerId });
  },

  reset({ commit }) {
    setOrganisation(null);
    commit("RESET");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
